import { isWalkable } from './isWalkable';

const BARRED_DOOR_POPUP_TEXTS = [
  'The door is barred from the other side. You can hear a slow, heavy breathing.',
  'The lock turns halfway, then stops. Through the keyhole, something wet blinks back at you.',
  'You throw your weight against it, but it will not budge. A note is nailed to the wood: “Do not let it out.”',
  'The door is sealed with a faded sigil drawn in ash and wax. Your hand goes numb when you touch it.',
  'Thick iron nails pin the door shut. From the cracks comes the sweet, sick smell of rot.',
  'The handle rattles on its own, once... twice... then goes still.',
  'The door opens a finger’s width, then slams itself shut as if something pulled it from within.',
  'The wood is warm. Too warm. A slow heartbeat seems to pulse through the grain.',
  'You find a chain wound tight around the handle and bolted into the wall. The links tremble as you approach.',
  'Chalk symbols cover the frame, layered over older symbols scratched deep into the stone beneath.',
  'There is no handle on this side. Only deep gouges, as if something tried to claw its way out.',
];

let lastBarredDoorPopupText = null;

export function pickBarredDoorPopupText() {
  const fallback =
    'The door is barred from the other side. You can hear a slow, heavy breathing.';

  const pool = [...new Set((BARRED_DOOR_POPUP_TEXTS || []).filter(Boolean))];

  if (!pool.length) return fallback;

  if (pool.length === 1) {
    lastBarredDoorPopupText = pool[0];
    return pool[0];
  }

  let choices = pool.filter((t) => t !== lastBarredDoorPopupText);
  if (!choices.length) choices = pool;

  const picked = choices[Math.floor(Math.random() * choices.length)];
  lastBarredDoorPopupText = picked;
  return picked;
}

export const BARRED_DOOR_MIN_SPAWNS_PER_FLOOR = 0;
export const BARRED_DOOR_MAX_SPAWNS_PER_FLOOR = 1;

export function makeSeededRand(seed0) {
  let a = (Number(seed0) || 0) >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function isBarredDoorCandidateTile(tile) {
  return tile === 'door-locked' || tile === 'door-closed';
}

export function buildBarredDoorKeysForFloor(map, { spawn, floor, runSeed } = {}) {
  if (!Array.isArray(map) || !map.length || !Array.isArray(map[0])) return new Set();

  const h = map.length;
  const w = map[0].length;
  const inBounds = (x, y) => x >= 0 && x < w && y >= 0 && y < h;
  const dirs = [
    { dx: 1, dy: 0, key: 'E' },
    { dx: -1, dy: 0, key: 'W' },
    { dx: 0, dy: 1, key: 'S' },
    { dx: 0, dy: -1, key: 'N' },
  ];

  let stepsDown = null;
  for (let y = 0; y < h && !stepsDown; y++) {
    for (let x = 0; x < w; x++) {
      if (map[y][x] === 'steps-down') {
        stepsDown = { x, y };
        break;
      }
    }
  }

  if (!spawn || !stepsDown || !inBounds(spawn.x, spawn.y)) return new Set();

  const isPathable = (x, y, blockedDoorKey = null) => {
    if (!inBounds(x, y)) return false;
    const k = `${x},${y}`;
    if (blockedDoorKey && k === blockedDoorKey) return false;

    const t = map[y][x];
    if (t === 'non-walkable') return false;

    if (t === 'door-locked' || t === 'door-open' || t === 'door-closed') return true;

    try {
      return !!isWalkable(t);
    } catch {
      return false;
    }
  };

  const hasPath = (from, to, blockedDoorKey = null) => {
    if (!from || !to) return false;
    if (!isPathable(from.x, from.y, blockedDoorKey)) return false;
    if (!isPathable(to.x, to.y, blockedDoorKey)) return false;

    const q = [from];
    let qi = 0;
    const seen = new Set([`${from.x},${from.y}`]);

    while (qi < q.length) {
      const cur = q[qi++];
      if (cur.x === to.x && cur.y === to.y) return true;

      for (const d of dirs) {
        const nx = cur.x + d.dx;
        const ny = cur.y + d.dy;
        if (!isPathable(nx, ny, blockedDoorKey)) continue;
        const nk = `${nx},${ny}`;
        if (seen.has(nk)) continue;
        seen.add(nk);
        q.push({ x: nx, y: ny });
      }
    }
    return false;
  };

  const oppositePair = (neighbors) => {
    const has = new Set(neighbors.map((n) => n.dir));
    if (has.has('N') && has.has('S')) {
      return [
        neighbors.find((n) => n.dir === 'N'),
        neighbors.find((n) => n.dir === 'S'),
      ];
    }
    if (has.has('E') && has.has('W')) {
      return [
        neighbors.find((n) => n.dir === 'E'),
        neighbors.find((n) => n.dir === 'W'),
      ];
    }
    return null;
  };

  const safeCandidates = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tile = map[y][x];
      if (!isBarredDoorCandidateTile(tile)) continue;

      const doorKey = `${x},${y}`;

      if (!hasPath(spawn, stepsDown, doorKey)) continue;

      const neighbors = [];
      for (const d of dirs) {
        const nx = x + d.dx;
        const ny = y + d.dy;
        if (!isPathable(nx, ny, doorKey)) continue;
        neighbors.push({ x: nx, y: ny, dir: d.key });
      }
      if (neighbors.length !== 2) continue;

      const pair = oppositePair(neighbors) || [neighbors[0], neighbors[1]];
      if (!pair?.[0] || !pair?.[1]) continue;

      if (!hasPath(pair[0], pair[1], doorKey)) continue;

      safeCandidates.push(doorKey);
    }
  }

  if (!safeCandidates.length) return new Set();

  const minN = Math.max(0, Math.floor(Number(BARRED_DOOR_MIN_SPAWNS_PER_FLOOR) || 0));
  const maxN = Math.max(minN, Math.floor(Number(BARRED_DOOR_MAX_SPAWNS_PER_FLOOR) || 0));
  const seeded = makeSeededRand(
    ((Number(runSeed) || 0) ^ ((Number(floor) || 1) * 0x45d9f3b) ^ 0xA5B35705) >>> 0
  );

  const desired =
    minN >= maxN ? minN : minN + Math.floor(seeded() * (maxN - minN + 1));

  const shuffled = safeCandidates.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seeded() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return new Set(shuffled.slice(0, Math.min(desired, shuffled.length)));
}